<template>
  <WdsModal :show="show" data-test="custom-field-edit-modal" @cancel="cancel">
    <template #header>
      <CustomFieldModalHeader :applies-to="appliesTo" :is-edit="true" />
    </template>
    <div v-show="errorBanner">{{ errorBanner }}</div>

    <CustomFieldContent
      v-if="!loadingFieldDetails && field"
      v-model:field="field"
      :show="show"
      :loading="updateInProgress"
      :validation-errors="validationErrors"
      :applies-to="appliesTo"
      :worker-onboarding-enabled="workerOnboardingEnabled"
      :is-supplier="isSupplier"
      :is-company="isCompany"
      @field-type-changed="resetErrors"
    ></CustomFieldContent>
    <div v-else class="tw-flex tw-justify-between">
      <WdsSpinner></WdsSpinner>
    </div>

    <template #footer>
      <div class="tw-flex tw-justify-between">
        <WdsButton @click="cancel">
          {{ $t('Cancel') }}
        </WdsButton>

        <WdsButton
          primary
          data-test="save-custom-field"
          :disabled="loadingFieldDetails"
          @click="update"
        >
          {{ $t('Save') }}
        </WdsButton>
      </div>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import { Ref, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { WdsModal, WdsButton, WdsSpinner } from '@worksome/wds-components'
import { isApolloError } from '@apollo/client/core'
import { $t } from '@/composables/translation'
import CustomFieldContent from '@/components/Fields/CustomFieldContent.vue'
import {
  extractFieldsGqlVariables,
  newInstanceOfCustomField,
  restoreSavedOptions,
} from '@/components/Fields/utils/CustomFieldUtils'
import { UpdateCustomFieldMutation } from '@/components/Fields/graphql/mutations'
import { CustomFieldDetailQuery } from '@/components/Fields/graphql/queries'
import { CustomFieldDetailFragment } from '@/components/Fields/graphql/fragments'
import { FormValidation } from '@/utils/formValidation'
import enums from '@/utils/enum'
import { CustomField } from '@/components/Fields/dataObjects/CustomField'
import { CustomFieldOption } from '@/components/Fields/dataObjects/CustomFieldOption'
import { NetworkError } from '@/utils/notification'
import { CustomFieldVisibility, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { useFragment } from '@/gql'
import CustomFieldModalHeader from '@/components/Fields/CustomFieldModalHeader.vue'

interface Props {
  fieldId: string
  appliesTo: TypeSupportingCustomFieldValues
  show: boolean
  visibility: CustomFieldVisibility
  workerOnboardingEnabled?: boolean
  isSupplier?: boolean
  isCompany?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits(['cancel', 'success'])

const {
  result,
  loading: loadingFieldDetails,
  onResult,
} = useQuery(
  CustomFieldDetailQuery,
  {
    id: props.fieldId,
  },
  {
    fetchPolicy: 'network-only',
    clientId: 'publicApiCustomFields',
  },
)

const field: Ref<CustomField> = ref(
  newInstanceOfCustomField({ appliesTo: props.appliesTo, visibility: props.visibility }),
)
const validationErrors = ref({})
// The options as last loaded, to put back any the server refused to remove.
const savedOptions: Ref<CustomFieldOption[]> = ref([])

const errorBanner = ref(null)

onResult(() => {
  const customFieldDetails = result?.value?.customField
  if (customFieldDetails) {
    const customFieldDetailsTyped = useFragment(CustomFieldDetailFragment, customFieldDetails)
    field.value = newInstanceOfCustomField(
      customFieldDetailsTyped,
      customFieldDetailsTyped.fieldType,
    )
    savedOptions.value = [...field.value.customFieldOptions]
    return
  }
  field.value = newInstanceOfCustomField({
    appliesTo: props.appliesTo,
    visibility: props.visibility,
  })
})

const {
  mutate: updateCustomField,
  loading: updateInProgress,
  onDone,
  onError,
} = useMutation(UpdateCustomFieldMutation, { clientId: 'publicApiCustomFields' })

async function update() {
  try {
    // Validation errors are shown on the form and anything else is reported
    // by onError, so the global error toast is skipped.
    await updateCustomField(
      {
        input: {
          customField: field.value.id!,
          ...extractFieldsGqlVariables(field),
        },
      },
      { context: { skipGlobalErrorHandling: true } },
    )
  } catch (exception: any) {
    if (
      isApolloError(exception) &&
      FormValidation.containsFormValidations(exception.graphQLErrors)
    ) {
      validationErrors.value = FormValidation.fromGraphqlErrors(exception.graphQLErrors) ?? {}
    } else {
      new NetworkError({
        msg: $t(`An error occurred when trying to edit a custom field`),
        severity: enums.notification.severity.CRITICAL,
        ex: exception,
      }).dispatch()
    }
  }
}

onError(({ graphQLErrors }) => {
  if (FormValidation.containsFormValidations(graphQLErrors)) {
    validationErrors.value = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}

    // Options rate cards key on were refused, and nothing was saved: show them again.
    if ('input.options' in validationErrors.value) {
      field.value.customFieldOptions = restoreSavedOptions(
        field.value.customFieldOptions,
        savedOptions.value,
      )
    }
  } else {
    new NetworkError({
      msg: $t('An error occurred when trying to edit a custom field'),
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  }
})

function resetErrors() {
  errorBanner.value = null
  validationErrors.value = {}
}

function reset() {
  resetErrors()
}

function cancel() {
  reset()
  emit('cancel')
}

onDone(() => {
  reset()
  emit('success')
})
</script>
